import StorePreviewCard from '@/components/profile/StorePreviewCard'
import EmptyState from '@/components/ui/EmptyState'
import { useAllCards } from '@/hooks/useCards'
import { useAllProfiles } from '@/hooks/useProfile'
import {
  STORE_SORT_OPTIONS,
  type StoreSort,
  buildStoreDirectory,
  sortStoreDirectory,
} from '@/lib/storeDirectory'
import {Compass, Store} from 'lucide-react'
import { useMemo, useState } from 'react'

function Discover() {
  const { profiles, loading: storesLoading } = useAllProfiles()
  const { cards, loading: cardsLoading } = useAllCards()
  const [sort, setSort] = useState<StoreSort>('recent')
  const loading = storesLoading || cardsLoading

  const directory = useMemo(() => buildStoreDirectory(profiles, cards), [cards, profiles])
  const newStores = useMemo(() => sortStoreDirectory(directory, 'newest').slice(0, 8), [directory])
  const browsed = useMemo(() => sortStoreDirectory(directory, sort), [directory, sort])

  return (
    <div className="mx-auto max-w-6xl px-4 pb-14 pt-6 sm:px-6 sm:pt-8">
      <div className="mb-6">
        <div className="mb-3 inline-flex items-center gap-2 rounded-full border-2 border-[#3B4CCA] bg-[#3B4CCA] px-3 py-1 text-xs font-extrabold uppercase tracking-wide text-white shadow-[0_3px_0_#24308a]">
          <Compass className="h-3.5 w-3.5" />
          Discover stores
        </div>
        <h1 className="font-display text-3xl font-extrabold text-white sm:text-5xl">Find a shop to browse</h1>
        <p className="mt-2 max-w-xl text-sm font-semibold text-white/55">
          Browse brand-new storefronts and the full directory from A to Z. New card listings live on the feed.
        </p>
      </div>

      {loading ? (
        <div className="grid gap-3 sm:grid-cols-2">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="h-24 animate-pulse rounded-[24px] bg-white/5" />
          ))}
        </div>
      ) : directory.length === 0 ? (
        <EmptyState
          icon={Store}
          title="No stores yet"
          description="When collectors create shop names, they will show up here to browse."
        />
      ) : (
        <div className="space-y-10">
          <section>
            <div className="mb-3 flex items-center gap-2">
              <Store className="h-4 w-4 text-[#FF5A5A]" />
              <h2 className="font-display text-2xl font-extrabold text-white">New stores</h2>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {newStores.map(item => (
                <StorePreviewCard key={`new-${item.profile._id}`} item={item} badge="New" />
              ))}
            </div>
          </section>

          <section>
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="font-display text-2xl font-extrabold text-white">Browse all stores</h2>
                <p className="mt-1 text-sm font-semibold text-white/50">
                  {browsed.length} {browsed.length === 1 ? 'shop' : 'shops'}
                </p>
              </div>
              <label className="block text-xs font-extrabold uppercase tracking-wide text-white/45">
                Sort
                <select
                  value={sort}
                  onChange={event => setSort(event.target.value as StoreSort)}
                  className="mt-1 block w-full rounded-2xl border-2 border-white/15 bg-black/60 px-3 py-2 text-sm font-extrabold text-white outline-none focus:border-[#FFDE00] sm:min-w-[220px]"
                >
                  {STORE_SORT_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {browsed.map(item => (
                <StorePreviewCard key={item.profile._id} item={item} />
              ))}
            </div>
          </section>
        </div>
      )}
    </div>
  )
}

export default Discover
